import { isEqual } from "lodash";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useUpdateAdminConfigurationMutation } from "../../../../redux/api/settings/settingsAPI";
import { useUpdateUserConfigurationMutation } from "../../../../redux/api/user/userAPI";
import { selectAuth } from "../../../../redux/features/auth/auth-slice";
import { selectProfile } from "../../../../redux/features/user/user-slice";
import { EConfigurationType } from "../../../../shared/oversight-core/enums/configuration-type";
import { EConfigurations } from "../../../../shared/oversight-core/enums/configurations";
import { EFeatureType } from "../../../../shared/oversight-core/enums/feature-type";
import { UserRole } from "../../../../shared/oversight-core/enums/user-roles";
import { IBrandPriority } from "../../../../shared/oversight-core/interfaces/brand-priority";
import { Configurations } from "../../../../shared/oversight-core/interfaces/configurations";
import AppSwitch from "../../../../shared/oversight-core/ui-elements/app-switch/app-switch";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import SpinnerModal from "../../../../shared/oversight-core/ui-elements/spinner/spinner";
import { configurationDescription } from "../../../../shared/oversight-core/utils/configuration-description";
import translate from "../../../../shared/oversight-core/utils/translate";
import BrandPriorityChangeModal from "../brand-priority-change-modal/brand-priority-change-modal";

interface IExtendedBrandPriority extends IBrandPriority {
  id: number;
}

interface IProps extends ModelContainerProps {
  configurations?: Configurations;
  sendConfigurations?: (Configurations: Configurations) => void;
  setHasChanged: React.Dispatch<React.SetStateAction<boolean>>;
}

const PreferencesModal = (props: IProps) => {
  const { show, configurations, sendConfigurations, setHasChanged, ...rest } =
    props;

  const auth = useSelector(selectAuth);
  const profile = useSelector(selectProfile);

  const [updateConfigurations, setUpdateConfigurations] =
    useState<Configurations>({} as Configurations);
  const [isShowBrandPriorityChangeModal, setIsShowBrandPriorityChangeModal] =
    useState(false);
  const [brands, setBrands] = useState<IExtendedBrandPriority[]>([]);

  const [triggerUpdateUserPreferences, { isLoading: isUpdatingPreferences }] =
    useUpdateUserConfigurationMutation();
  const [
    triggerUpdateAdminConfiguration,
    { isLoading: isUpdatingAdminPreferences },
  ] = useUpdateAdminConfigurationMutation();

  useEffect(() => {
    if (configurations) {
      setUpdateConfigurations(configurations);
    }
  }, [configurations]);

  useEffect(() => {
    const list = configurations?.BRAND_PRIORITY_LIST;
    setBrands(
      Array.isArray(list)
        ? list.map(
            (bp, index): IExtendedBrandPriority => ({
              ...bp,
              id: index,
            })
          )
        : []
    );
  }, [configurations]);

  const onPreferenceChange = (
    configuration: EConfigurations,
    newValue: boolean | IBrandPriority[]
  ) => {
    const updatedConfigurations = Object.fromEntries(
      Object.entries(updateConfigurations).map(([key, value]) => [
        key,
        key === configuration ? newValue : value,
      ])
    );

    setUpdateConfigurations(updatedConfigurations as Configurations);
  };

  const onConfirm = async () => {
    if (auth.token && profile?.role) {
      await (profile.role === UserRole.ROLE_USER
        ? triggerUpdateUserPreferences
        : triggerUpdateAdminConfiguration)({
        configurationType:
          profile.role === UserRole.ROLE_USER
            ? EConfigurationType.ROLE_BASED
            : EConfigurationType.SYSTEM_BASED,
        featureType: EFeatureType.SOLAR_SIM,
        role: profile.role,
        configurations: updateConfigurations,
      })
        .unwrap()
        .then(() => {
          const isChanged = !isEqual(updateConfigurations, configurations);
          setHasChanged(isChanged);
          sendConfigurations?.(updateConfigurations);
          props.onConfirm?.();
          props.onClose?.();
        });
      return;
    }
    sendConfigurations?.(updateConfigurations);
    props.onConfirm?.();
    props.onClose?.();
  };

  return (
    <>
      <ModalContainer
        title="Preferences"
        {...rest}
        show={
          show &&
          !(
            isUpdatingPreferences ||
            isUpdatingAdminPreferences ||
            isShowBrandPriorityChangeModal
          )
        }
        size="modal-md"
        onConfirm={onConfirm}
      >
        <>
          <Row>
            <Col className="title-3 font-weight-500">
              System Design Preferences
            </Col>
          </Row>
          {Object.entries(updateConfigurations).map(
            ([configuration, value]) => {
              if (configuration === EConfigurations.BRAND_PRIORITY_LIST) {
                return null;
              }

              return (
                <Row className="mt-3" key={configuration}>
                  <Col>
                    <Row>
                      <Col className="title-2 font-weight-500">
                        {translate(configuration)}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="text-color-2 font-size-12">
                        {configurationDescription(
                          configuration as EConfigurations
                        )}
                      </Col>
                    </Row>
                    {configuration === EConfigurations.BRAND_PRIORITY && (
                      <Row>
                        <Col
                          className="col-auto title-3 font-size-12 text-decoration-underline cursor-pointer"
                          onClick={() => {
                            setIsShowBrandPriorityChangeModal(true);
                          }}
                        >
                          Change Brand Priority
                        </Col>
                      </Row>
                    )}
                  </Col>
                  <Col className="col-auto">
                    <AppSwitch
                      isOn={typeof value === "boolean" && value}
                      size="sm"
                      onSwitch={(value: boolean) => {
                        onPreferenceChange(
                          configuration as EConfigurations,
                          value
                        );
                      }}
                    />
                  </Col>
                </Row>
              );
            }
          )}
        </>
      </ModalContainer>
      <BrandPriorityChangeModal
        show={isShowBrandPriorityChangeModal}
        onCancel={() => {
          setIsShowBrandPriorityChangeModal(false);
        }}
        onClose={() => {
          setIsShowBrandPriorityChangeModal(false);
        }}
        onConfirm={() => {
          onPreferenceChange(
            EConfigurations.BRAND_PRIORITY_LIST,
            brands.map((brand, index) => {
              return {
                brand: brand.brand,
                priority: index + 1,
              };
            })
          );
          setIsShowBrandPriorityChangeModal(false);
        }}
        brands={brands}
        setBrands={setBrands}
      />
      <SpinnerModal
        show={isUpdatingPreferences || isUpdatingAdminPreferences}
      />
    </>
  );
};

export default PreferencesModal;
